import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';

export interface BaseInteractiveProps {
  exercise: any;
  onAnswer: (answer: any) => void;
  showFeedback?: boolean;
  disabled?: boolean;
}

// Hotspot Component (click on image areas)
export const HotspotComponent: React.FC<BaseInteractiveProps> = ({
  exercise,
  onAnswer,
  showFeedback = false,
  disabled = false
}) => {
  // Initialize with any pre-selected hotspots
  const [selectedHotspots, setSelectedHotspots] = useState<number[]>(
    exercise.initialSelections || []
  );
  const [hasAnswered, setHasAnswered] = useState(false);

  useEffect(() => {
    if (exercise.initialSelections) {
      setSelectedHotspots(exercise.initialSelections);
    }
  }, [exercise.initialSelections]);

  const handleHotspotClick = (index: number) => {
    if (disabled || hasAnswered) return;

    const newSelected = exercise.multiSelect
      ? selectedHotspots.includes(index)
        ? selectedHotspots.filter((i) => i !== index)
        : [...selectedHotspots, index]
      : [index];

    setSelectedHotspots(newSelected);
    setHasAnswered(true);
    onAnswer(newSelected);
  };

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <p className="text-gray-700 mb-3">{exercise.content}</p>
        <p className="font-medium text-gray-900 mb-3">{exercise.question}</p>
      </div>

      <div className="relative inline-block">
        {exercise.imageUrl ? (
          <img
            src={exercise.imageUrl}
            alt={exercise.imageAlt || 'Interactive diagram'}
            className="max-w-full h-auto border border-gray-200 rounded-lg"
          />
        ) : (
          <div className="w-full h-64 bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center">
            <span className="text-gray-500">Image placeholder</span>
          </div>
        )}

        {/* Hotspot overlays */}
        {exercise.hotspots?.map((hotspot: any, index: number) => (
          <button
            key={index}
            onClick={() => handleHotspotClick(index)}
            disabled={disabled || hasAnswered}
            className={`absolute w-6 h-6 rounded-full border-2 ${
              selectedHotspots.includes(index)
                ? 'bg-blue-600 border-blue-600'
                : 'bg-white border-blue-600 hover:bg-blue-50'
            } ${disabled || hasAnswered ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            style={{
              left: `${hotspot.x}%`,
              top: `${hotspot.y}%`,
              transform: 'translate(-50%, -50%)'
            }}
          >
            <span
              className={`text-xs font-bold ${
                selectedHotspots.includes(index) ? 'text-white' : 'text-blue-600'
              }`}
            >
              {index + 1}
            </span>
          </button>
        ))}
      </div>

      {showFeedback && selectedHotspots.length > 0 && exercise.explanation && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-800 mb-1">Explanation:</p>
              <p className="text-sm text-blue-700">{exercise.explanation}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
